<template>
    <app-layout title="Summary Dokumen Pengadaan - Monitoring">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Summary Dokumen Pengadaan (Monitoring)
                <span v-if="isUnitScoped" class="text-sm font-normal text-gray-600">(Unit)</span>
            </h2>
        </template>

        <div class="py-4">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <!-- Back Button -->
                <div class="mb-4">
                    <Link :href="route('monitoring_pimpinan.dashboard')" 
                          class="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">
                        <i class="pi pi-arrow-left mr-2"></i>
                        Kembali ke Dashboard
                    </Link>
                </div>
                
                <div class="bg-white shadow-xl sm:rounded-lg">
                    <section class="text-gray-600 body-font">
                        <div class="container px-5 py-5 mx-auto">
                            <!-- Tombol Print -->
                            <div class="mb-4 print-hidden flex justify-end">
                                <button @click="printPage" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                                    Print
                                </button>
                            </div>

                            <!-- Status Section -->
                            <div class="mb-6">
                                <h3 class="font-bold text-lg mb-2">Status</h3>
                                <StatusDokumenPengadaanInfo :status="dokumenPengadaan.status" />
                            </div>

                            <!-- Unit Kerja Section (if applicable) -->
                            <div class="mb-6" v-if="paketPengadaan && paketPengadaan.unitKerja">
                                <h3 class="font-bold text-lg mb-2">Unit Kerja</h3>
                                <div class="p-2 border rounded-md bg-blue-50">
                                    {{ paketPengadaan.unitKerja.nama_unit || paketPengadaan.unit }}
                                </div>
                            </div>

                            <!-- Paket Pengadaan Section -->
                            <div class="mb-6">
                                <h3 class="font-bold text-lg mb-2">Paket Pengadaan</h3>
                                <div class="grid grid-cols-1 gap-4">
                                    <div class="field w-full mb-4">
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div class="field">
                                                <JetLabel value="Nama Paket Pengadaan" />
                                                <div class="p-2 border rounded-md">
                                                    {{ paketPengadaan.nama_paket_pengadaan }}
                                                </div>
                                            </div>
                                            <div class="field">
                                                <JetLabel value="Kategori Pengadaan" />
                                                <div class="p-2 border rounded-md">
                                                    {{ paketPengadaan.kategori_pengadaan }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="field w-full mb-4">
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div class="field">
                                                <JetLabel value="Pagu Anggaran" />
                                                <div class="p-2 border rounded-md">
                                                    {{ formatCurrency(paketPengadaan.pagu_anggaran) }}
                                                </div>
                                            </div>
                                            <div class="field">
                                                <JetLabel value="Jenis Pengadaan" />
                                                <div class="p-2 border rounded-md">
                                                    {{ paketPengadaan.jenis_pengadaan_nama || paketPengadaan.jenis_pengadaan }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="field w-full mb-4">
                                        <JetLabel value="Ruang Lingkup Pengadaan" />
                                        <div class="p-2 border rounded-md">
                                            {{ paketPengadaan.ruang_lingkup_pengadaan }}
                                        </div>
                                    </div>
                                    <div class="field w-full mb-4">
                                        <JetLabel value="Spesifikasi Pekerjaan" />
                                        <div class="p-2 border rounded-md">
                                            {{ paketPengadaan.spesifikasi_pekerjaan }}
                                        </div>
                                    </div>
                                    <div class="field w-full mb-4">
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div class="field">
                                                <JetLabel value="Metode Pemilihan" />
                                                <div class="p-2 border rounded-md">
                                                    {{ paketPengadaan.metode_pemilihan_nama || paketPengadaan.metode_pemilihan }}
                                                </div>
                                            </div>
                                            <div class="field">
                                                <JetLabel value="MAK" />
                                                <div class="p-2 border rounded-md">
                                                    {{ paketPengadaan.mak }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Jadwal Section -->
                            <div class="mb-6">
                                <h3 class="font-bold text-lg mb-2">Jadwal</h3>
                                <DataTable :value="jadwal" responsiveLayout="scroll">
                                    <Column field="kode_jadwal" header="Kode Jadwal">
                                        <template #body="slotProps">
                                            {{ slotProps.data.jadwal && slotProps.data.jadwal.nama_jadwal ? slotProps.data.jadwal.nama_jadwal : toTitleCase(slotProps.data.kode_jadwal) }}
                                        </template>
                                    </Column>
                                    <Column field="waktu_mulai" header="Tanggal Mulai">
                                        <template #body="slotProps">
                                            {{ formatDate(slotProps.data.waktu_mulai) }}
                                        </template>
                                    </Column>
                                    <Column field="waktu_selesai" header="Tanggal Selesai">
                                        <template #body="slotProps">
                                            {{ formatDate(slotProps.data.waktu_selesai) }}
                                        </template>
                                    </Column>
                                    <Column field="tempat" header="Tempat"></Column>
                                    <Column field="keterangan" header="Keterangan"></Column>
                                </DataTable>
                            </div>

                            <!-- File Dokumen Section -->
                            <div class="mb-6">
                                <h3 class="font-bold text-lg mb-2">File Dokumen</h3>
                                <DataTable :value="fileDokumen" responsiveLayout="scroll" autoLayout class="file-dokumen-table" :tableStyle="'table-layout: fixed; width: 100%;'">
                                    <Column field="nama_file_asli" header="Nama File" :bodyStyle="{maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}"></Column>
                                    <Column field="keterangan" header="Keterangan" :bodyStyle="{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}"></Column>
                                    <Column field="mime" header="Tipe File" :bodyStyle="{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}"></Column>
                                    <Column field="size" header="Ukuran">
                                        <template #body="slotProps">
                                            {{ formatFileSize(slotProps.data.size) }}
                                        </template>
                                    </Column>
                                    <Column header="File Dokumen Prosedur" :bodyStyle="{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}">
                                        <template #body="slotProps">
                                            {{ slotProps.data.file_dokumen_prosedur ? slotProps.data.file_dokumen_prosedur.nama_file : '-' }}
                                        </template>
                                    </Column>
                                    <Column header="Aksi" :bodyStyle="{width: '90px', textAlign: 'center'}">
                                        <template #body="slotProps">
                                            <a :href="'/file/' + slotProps.data.id + '/download'" target="_blank" class="text-blue-600 hover:text-blue-800">
                                                <Button label="Download" icon="pi pi-download" class="p-button-sm p-button-outlined" />
                                            </a>
                                        </template>
                                    </Column>
                                </DataTable>
                            </div>

                            <!-- HPS Section -->
                            <div class="mb-6">
                                <h3 class="font-bold text-lg mb-2">HPS (Harga Perkiraan Sendiri)</h3>
                                <div class="mb-4">
                                    <JetLabel value="Total HPS" />
                                    <div class="p-2 border rounded-md font-bold text-lg">
                                        {{ formatCurrency(dokumenPengadaan.total_hps) }}
                                    </div>
                                </div>
                                <DataTable :value="hps" responsiveLayout="scroll">
                                    <Column field="urutan" header="No"></Column>
                                    <Column field="nama_barang_jasa" header="Nama Barang/Jasa"></Column>
                                    <Column field="merk" header="Merk"></Column>
                                    <Column field="tipe" header="Tipe"></Column>
                                    <Column field="spesifikasi" header="Spesifikasi"></Column>
                                    <Column field="satuan" header="Satuan"></Column>
                                    <Column field="volume" header="Volume"></Column>
                                    <Column field="harga_satuan" header="Harga Satuan">
                                        <template #body="slotProps">
                                            {{ formatCurrency(slotProps.data.harga_satuan) }}
                                        </template>
                                    </Column>
                                    <Column header="Total">
                                        <template #body="slotProps">
                                            {{ formatCurrency(slotProps.data.volume * slotProps.data.harga_satuan) }}
                                        </template>
                                    </Column>
                                    <Column field="catatan" header="Catatan"></Column>
                                </DataTable>
                            </div>

                            <!-- Penawaran Section -->
                            <div class="mb-6" v-if="dokumenPengadaan.status === 'selesai'">
                                <h3 class="font-bold text-lg mb-2">Penawaran</h3>
                                <div v-if="penawaran && penawaran.length > 0">
                                    <DataTable :value="penawaran" responsiveLayout="scroll">
                                        <Column field="penyedia.nama_perusahaan" header="Nama Perusahaan"></Column>
                                        <Column field="penyedia.alamat_perusahaan" header="Alamat"></Column>
                                        <Column header="NPWP">
                                            <template #body="slotProps">
                                                {{ slotProps.data.penyedia?.npwp_perusahaan || '-' }}
                                            </template>
                                        </Column>
                                        <Column header="Evaluasi">
                                            <template #body="slotProps">
                                                <span :class="slotProps.data.is_lulus_evaluasi ? 'text-green-600' : 'text-red-600'">
                                                    {{ slotProps.data.is_lulus_evaluasi ? 'Lulus' : 'Tidak Lulus' }}
                                                </span>
                                            </template>
                                        </Column>
                                        <Column field="total_penawaran" header="Total Penawaran">
                                            <template #body="slotProps">
                                                {{ formatCurrency(slotProps.data.harga_penawaran) }}
                                            </template>
                                        </Column>
                                        <Column header="Pemenang">
                                            <template #body="slotProps">
                                                <i v-if="slotProps.data.is_pemenang" class="fas fa-trophy text-yellow-500"></i>
                                                <span v-else>-</span>
                                            </template>
                                        </Column>
                                    </DataTable>
                                </div>
                                <div v-else class="flex flex-col justify-center items-center p-4 text-center">
                                    <div class="text-2xl font-bold text-gray-700 mb-2">
                                        Jumlah Penawaran: 0
                                    </div>
                                    <div class="text-gray-500 italic">
                                        Belum ada penawaran untuk dokumen pengadaan ini
                                    </div>
                                </div>
                            </div>

                            <!-- Pemenang Section -->
                            <div class="mb-6" v-if="pemenang && dokumenPengadaan.status === 'selesai'">
                                <h3 class="font-bold text-lg mb-2">Pemenang</h3>
                                <div class="p-4 border rounded-md bg-green-50">
                                    <p><strong>Nama Perusahaan:</strong> {{ pemenang.penyedia?.nama_perusahaan }}</p>
                                    <p><strong>Total Penawaran:</strong> {{ formatCurrency(getTotalNilaiPenawaran(pemenang)) }}</p>
                                    <p v-if="pemenang.tanggal_penetapan_pemenang"><strong>Tanggal Penetapan:</strong> {{ formatDate(pemenang.tanggal_penetapan_pemenang) }}</p>
                                </div>
                            </div>

                            <!-- Sanggah Section -->
                            <div class="mb-6">
                                <h3 class="font-bold text-lg mb-2">Sanggah</h3>
                                <div v-if="sanggah && sanggah.length > 0">
                                    <DataTable :value="sanggah" responsiveLayout="scroll" class="sanggah-table">
                                        <Column header="No" :bodyStyle="{width: '60px'}">
                                            <template #body="slotProps">{{ slotProps.index + 1 }}</template>
                                        </Column>
                                        <Column field="penyedia" header="Penyedia"></Column>
                                        <Column field="alasan" header="Alasan"></Column>
                                        <Column field="status" header="Status"></Column>
                                        <Column field="jawaban" header="Jawaban"></Column>
                                    </DataTable>
                                </div>
                                <div v-else class="text-gray-500 italic">Tidak ada sanggah untuk dokumen pengadaan ini</div>
                            </div>

                            <!-- Penjelasan Section -->
                            <div class="mb-6">
                                <h3 class="font-bold text-lg mb-2">Penjelasan</h3>
                                <div v-if="penjelasan && penjelasan.length > 0">
                                    <div class="space-y-4">
                                        <div v-for="subject in penjelasan" :key="subject.id" class="border rounded-lg p-4 bg-gray-50">
                                            <div class="mb-3">
                                                <div class="flex items-center justify-between mb-2">
                                                    <h4 class="font-semibold text-lg text-gray-800">{{ subject.subject }}</h4>
                                                    <span class="text-xs text-gray-500">{{ formatDate(subject.created_at) }}</span>
                                                </div>
                                                <p class="text-sm text-gray-600 mb-2">{{ subject.description }}</p>
                                                <div class="text-xs text-gray-500">
                                                    Dibuat oleh: {{ subject.creator_name }}
                                                </div>
                                            </div>
                                            <!-- Attachments -->
                                            <div v-if="subject.attachments && subject.attachments.length > 0" class="mb-3">
                                                <h5 class="font-medium text-sm text-gray-700 mb-2">Lampiran:</h5>
                                                <div class="flex flex-wrap gap-2">
                                                    <div v-for="attachment in subject.attachments" :key="attachment.id" 
                                                         class="bg-white border rounded px-3 py-1 text-xs">
                                                        <a :href="attachment.download_url" target="_blank" class="text-blue-600 hover:text-blue-800">
                                                            <i class="pi pi-paperclip mr-1"></i>
                                                            {{ attachment.original_name }}
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- Messages -->
                                            <div v-if="subject.messages && subject.messages.length > 0" class="mt-3">
                                                <h5 class="font-medium text-sm text-gray-700 mb-2">Pesan ({{ subject.messages.length }}):</h5>
                                                <div class="space-y-2 max-h-60 overflow-y-auto">
                                                    <div v-for="message in subject.messages" :key="message.id" 
                                                         class="bg-white border rounded p-3 text-sm">
                                                        <div class="flex items-center justify-between mb-1">
                                                            <span class="font-medium text-gray-700">{{ message.display_name }}</span>
                                                            <span class="text-xs text-gray-500">{{ formatDate(message.created_at) }}</span>
                                                        </div>
                                                        <p class="text-gray-600">{{ message.message }}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-else class="text-xs text-gray-500 italic mt-2">
                                                Belum ada pesan untuk topik ini
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="flex flex-col justify-center items-center p-4 text-center">
                                    <div class="text-2xl font-bold text-gray-700 mb-2">
                                        Jumlah Topik Penjelasan: 0
                                    </div>
                                    <div class="text-gray-500 italic">
                                        Belum ada topik penjelasan untuk dokumen pengadaan ini
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<script>
import { defineComponent } from 'vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import JetLabel from '@/Jetstream/Label';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import StatusDokumenPengadaanInfo from '@/Components/StatusDokumenPengadaanInfo';
import { Link } from '@inertiajs/inertia-vue3';

export default defineComponent({
    components: {
        AppLayout,
        JetLabel,
        DataTable,
        Column,
        Button,
        StatusDokumenPengadaanInfo,
        Link
    },
    props: {
        dokumenPengadaan: Object,
        paketPengadaan: Object,
        jadwal: Array,
        fileDokumen: Array,
        hps: Array,
        penawaran: Array,
        penjelasan: Array,
        sanggah: Array,
        isUnitScoped: Boolean
    },
    computed: {
        pemenang() {
            return this.penawaran ? this.penawaran.find(p => p.is_pemenang) : null;
        }
    },
    methods: {
        printPage() {
            window.print();
        },
        formatDate(dateString) {
            if (!dateString) return '-';
            const options = { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            };
            return new Date(dateString).toLocaleDateString('id-ID', options);
        },
        formatCurrency(value) {
            if (!value) return 'Rp 0';
            return new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 0
            }).format(value);
        },
        formatFileSize(bytes) {
            if (!bytes) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        toTitleCase(str) {
            if (!str) return '';
            return str.replace(/_/g, ' ')
                      .replace(/\w\S*/g, function(txt) {
                          return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
                      });
        },
        getTotalNilaiPenawaran(pengajuan) {
            if (!pengajuan || !pengajuan.detail_penawaran_kepersertaan_pengadaan) return 0;
            return pengajuan.detail_penawaran_kepersertaan_pengadaan.reduce((sum, item) => sum + (item.volume * item.harga_satuan), 0);
        }
    }
});
</script>

<style scoped>
.print-hidden {
    display: block;
}
@media print {
    .print-hidden {
        display: none !important;
    }
    body {
        background: #fff !important;
    }
    .bg-white, .shadow-xl, .sm\:rounded-lg {
        background: #fff !important;
        box-shadow: none !important;
        border-radius: 0 !important;
    }
    .container, .max-w-7xl, .mx-auto, .sm\:px-6, .lg\:px-8, .py-4, .px-5, .py-5 {
        padding: 0 !important;
        margin: 0 !important;
        max-width: 100% !important;
    }
}
.file-dokumen-table :deep(.p-datatable-tbody) td,
.file-dokumen-table :deep(.p-datatable-thead) th {
    font-size: 0.85rem;
    padding: 0.35rem 0.5rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.file-dokumen-table :deep(.p-datatable) {
    font-size: 0.9rem;
}
</style>
